import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Download, Loader2, Sparkles, ArrowRight, FileText, BarChart2, Clock, Check, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_ICONS = { ebook: BookOpen, journal: BookOpen, prompts: FileText, tracker: BarChart2 };

/* --- Sub-components for each step --- */

const FormStep = ({ product, name, setName, email, setEmail, loading, onSubmit }) => {
  const Icon = PRODUCT_ICONS[product.type] || BookOpen;
  return (
    <form onSubmit={onSubmit} className="space-y-5 pt-2">
      <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
        <Icon size={20} style={{ color: "var(--ll-green)" }} />
        <div>
          <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{product.name}</p>
          <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            {product.description?.slice(0, 80)}{product.description?.length > 80 ? "..." : ""}
          </p>
        </div>
      </div>
      {product.features && (
        <ul className="space-y-1.5">
          {product.features.map((f) => (
            <li key={f} className="flex items-center gap-2 text-xs" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              <Check size={12} style={{ color: "var(--ll-green)" }} /> {f}
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-2">
        <Label htmlFor="free-dl-name" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Name</Label>
        <Input id="free-dl-name" data-testid="free-download-name-input" placeholder="Your name" value={name}
          onChange={(e) => setName(e.target.value)} className="rounded-xl border bg-white/50 focus:bg-white" style={{ borderColor: "var(--ll-border)" }} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="free-dl-email" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Email *</Label>
        <Input id="free-dl-email" data-testid="free-download-email-input" type="email" placeholder="you@example.com" required value={email}
          onChange={(e) => setEmail(e.target.value)} className="rounded-xl border bg-white/50 focus:bg-white" style={{ borderColor: "var(--ll-border)" }} />
      </div>
      <button type="submit" data-testid="free-download-submit-btn" disabled={loading}
        className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
        style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        {loading ? "Processing..." : "Get Your Free Copy"}
      </button>
      <p className="text-xs text-center" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>We respect your privacy. No spam, ever.</p>
    </form>
  );
};

const ExtraItemsList = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <div key={item.name} className="flex items-center justify-between p-3 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
        <div className="flex items-center gap-2.5">
          {item.status === "available" ? (
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--ll-green)" }}><Check size={14} style={{ color: "white" }} /></div>
          ) : (
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "var(--ll-bg-secondary)", border: "1px dashed var(--ll-text-muted)" }}><Clock size={12} style={{ color: "var(--ll-text-muted)" }} /></div>
          )}
          <span className="text-sm font-medium" style={{ color: item.status === "available" ? "var(--ll-text)" : "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{item.name}</span>
        </div>
        {item.status === "available" ? (
          <button onClick={() => window.open(item.download_url, "_blank")} data-testid={`download-${item.name.toLowerCase().replace(/\s/g, "-")}`}
            className="rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
            style={{ background: "var(--ll-green)", color: "white", fontFamily: "'Outfit', sans-serif" }}>
            <Download size={12} /> Download
          </button>
        ) : (
          <span className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full"
            style={{ background: "rgba(247,208,70,0.15)", color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}>Coming Soon</span>
        )}
      </div>
    ))}
  </div>
);

const UpsellCard = ({ upsell, onCheckout }) => (
  <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--ll-border)" }}>
    <div className="p-4" style={{ background: "linear-gradient(135deg, rgba(247,208,70,0.1), rgba(163,184,153,0.1))" }}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} style={{ color: "var(--ll-accent)" }} />
        <p className="text-[10px] tracking-[0.2em] uppercase font-medium" style={{ color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}>Upgrade to the full collection</p>
      </div>
      <h4 className="font-serif text-base font-medium mb-1" style={{ color: "var(--ll-text)" }}>{upsell.product_name}</h4>
      <p className="text-xs mb-3" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{upsell.description?.slice(0, 120)}</p>
      <ul className="space-y-1 mb-3">
        {upsell.features?.slice(0, 4).map((f) => (
          <li key={f} className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
            <Check size={10} style={{ color: "var(--ll-green)" }} /> {f}
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between">
        <span className="font-serif text-lg font-semibold" style={{ color: "var(--ll-text)" }}>R{upsell.price_zar}</span>
        <button data-testid="upsell-checkout-btn" onClick={() => onCheckout(upsell)}
          className="rounded-full px-5 py-2 text-xs font-medium flex items-center gap-1.5 transition-all hover:-translate-y-0.5 hover:shadow-lg"
          style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
          <ShoppingBag size={12} /> Get Full Collection
        </button>
      </div>
    </div>
  </div>
);

const SuccessStep = ({ responseData, onSeeOffer, onClose, onUpsellCheckout }) => (
  <div className="py-4 space-y-5">
    <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center" style={{ background: "var(--ll-green)" }}>
      <Download size={28} style={{ color: "white" }} />
    </div>
    <p className="text-sm text-center" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
      Your <strong>{responseData.product_name}</strong> is ready to download.
    </p>
    {responseData.extra_items ? (
      <ExtraItemsList items={responseData.extra_items} />
    ) : (
      <button onClick={() => window.open(responseData.download_url, "_blank")} data-testid="free-download-link"
        className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
        style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
        <Download size={16} /> Download {responseData.product_name} (PDF)
      </button>
    )}
    {responseData.upsell && <UpsellCard upsell={responseData.upsell} onCheckout={onUpsellCheckout} />}
    {responseData.discount_offer && (
      <button data-testid="free-download-see-offer-btn" onClick={onSeeOffer}
        className="w-full rounded-full px-6 py-3 text-sm font-medium border transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
        style={{ borderColor: "var(--ll-accent)", color: "var(--ll-text-secondary)", background: "rgba(247, 208, 70, 0.1)", fontFamily: "'Outfit', sans-serif" }}>
        <Sparkles size={16} style={{ color: "var(--ll-accent)" }} /> We have a special offer for you <ArrowRight size={14} />
      </button>
    )}
    <button data-testid="free-download-close" onClick={onClose}
      className="w-full text-xs text-center py-2 transition-colors hover:opacity-70"
      style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>No thanks, close</button>
  </div>
);

const OfferStep = ({ offer, onClaim, onBack }) => (
  <div className="py-4 space-y-5">
    <div className="p-5 rounded-xl text-center" style={{ background: "linear-gradient(135deg, rgba(247,208,70,0.15), rgba(163,184,153,0.15))" }}>
      <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}>Exclusive Reader Offer</p>
      <h3 className="font-serif text-xl font-medium mb-1" style={{ color: "var(--ll-text)" }}>{offer.product_name}</h3>
      <div className="flex items-center justify-center gap-3 mb-3">
        <span className="text-sm line-through" style={{ color: "var(--ll-text-muted)" }}>R{offer.original_price?.toLocaleString()}</span>
        <span className="font-serif text-3xl font-semibold" style={{ color: "var(--ll-text)" }}>R{offer.discount_price?.toLocaleString()}</span>
      </div>
      <p className="text-xs px-3 py-1 rounded-full inline-block" style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
        Save R{offer.savings?.toLocaleString()}
      </p>
    </div>
    <p className="text-sm text-center" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
      Ready to go deeper? The full course gives you 10 video lessons, worksheets, community access, and a certificate of completion.
    </p>
    <button data-testid="free-download-claim-discount-btn" onClick={() => onClaim(offer)}
      className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
      style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
      <Sparkles size={16} /> Claim Your Discount — R{offer.discount_price?.toLocaleString()}
    </button>
    <button data-testid="free-download-back" onClick={onBack}
      className="w-full text-xs text-center py-2 transition-colors hover:opacity-70"
      style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Back to download</button>
  </div>
);

/* --- Main Modal --- */

const FreeDownloadModal = ({ open, onClose, product, onCheckout }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form");
  const [responseData, setResponseData] = useState(null);

  if (!product) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email address."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/free-download/request`, { product_id: product.id, email, name });
      setResponseData(res.data);
      setStep("success");
      toast.success("Your download is ready!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setStep("form"); setEmail(""); setName(""); setResponseData(null); onClose(); };

  const handleUpsellCheckout = (upsell) => {
    if (onCheckout) onCheckout({ id: upsell.product_id, name: upsell.product_name, price: upsell.price_zar, price_zar: upsell.price_zar, price_usd: upsell.price_usd, type: "digital" });
    handleClose();
  };

  const handleDiscountCheckout = (offer) => {
    if (onCheckout) onCheckout({ id: offer.product_id, name: offer.product_name, price: offer.discount_price, original_price: offer.original_price, type: "course", use_discount: true });
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="free-download-modal" className="sm:max-w-md rounded-2xl border max-h-[90vh] overflow-y-auto"
        style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg)" }}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium" style={{ color: "var(--ll-text)" }}>
            {step === "form" && `Get ${product.name}`}
            {step === "success" && "Your Download is Ready!"}
            {step === "offer" && "Exclusive Offer For You"}
          </DialogTitle>
          <DialogDescription className="sr-only">Download your free {product.name}</DialogDescription>
        </DialogHeader>

        {step === "form" && <FormStep product={product} name={name} setName={setName} email={email} setEmail={setEmail} loading={loading} onSubmit={handleSubmit} />}
        {step === "success" && responseData && <SuccessStep responseData={responseData} onSeeOffer={() => setStep("offer")} onClose={handleClose} onUpsellCheckout={handleUpsellCheckout} />}
        {step === "offer" && responseData?.discount_offer && <OfferStep offer={responseData.discount_offer} onClaim={handleDiscountCheckout} onBack={() => setStep("success")} />}
      </DialogContent>
    </Dialog>
  );
};

export default FreeDownloadModal;
